/**
 * App store — Zustand, scoped to UI state + mutation-only outputs.
 *
 * Server-fetched state (documents, scenario, synthetic_locked, status,
 * progress entries) lives in the react-query cache via @/hooks/api.
 * What stays here:
 *  - sessionId pointer (which session this tab is bound to).
 *  - Agent mutation output (appealType/letterHtml/etc) — these come
 *    only from POST /run, not from any GET, so react-query has no
 *    natural place for them.
 *  - Local-only chat history for the refinement panel.
 *  - Pure UI: which view, which doc selected, drawer states, prefs.
 */

import { create } from "zustand";

import type {
  AgentStatus,
  AppealType,
  ChatMessage,
  DocumentKind,
  DocumentRequest,
  LetterRevisionInfo,
} from "@/api/types";

export type LetterMode = "letter" | "diff" | "editing";

type Tab = "formatted" | "fields" | "json" | "raw";

export type PendingMode = "upload" | "synthesize";

export interface PendingEntry {
  /** Caller-generated id so push/pop pair reliably when several
   * synth requests for different kinds are in flight at once. */
  token: string;
  kind: DocumentKind;
  mode: PendingMode;
}

interface AppState {
  // Session pointer
  sessionId: string | null;

  // Agent mutation output (POST /run / POST /letter/refine)
  agentStatus: AgentStatus;
  appealType: AppealType | null;
  appealTypeRationale: string | null;
  analysisSummary: string | null;
  letterHtml: string | null;
  pendingRequest: DocumentRequest | null;
  refinementMessages: ChatMessage[];

  // Letter revisions — append-only history with a movable head pointer.
  // ``letterHtml`` mirrors the html of the current revision (kept here
  // so existing consumers don't have to look up by id).
  revisions: LetterRevisionInfo[];
  currentRevisionId: string | null;
  letterMode: LetterMode;
  diffingRevisionId: string | null; // which revision we're viewing the diff of

  // UI-only
  view: "analysis" | "letter";
  // Which SidePanel tab is active. Lifted out of SidePanel so other
  // components (e.g. AgentPanel, after synthesizing a requested doc)
  // can steer the user back to the Documents tab.
  sideTab: "documents" | "fields" | "generate";
  selectedDocId: string | null;
  viewerTabByDoc: Record<string, Tab>;
  appealTypeDialogOpen: boolean;
  // Single-doc target carries the kind to synthesize after the user
  // picks an appeal type; bundle target tells the dialog to fan out into
  // the full sample-data set (driven from EmptyState).
  appealTypePickerTarget:
    | { mode: "single"; kind: string }
    | { mode: "bundle" }
    | null;
  drawerOpen: { rail: boolean; agent: boolean; chat: boolean };
  // When true, the main area is replaced by the Guide page. Cleared by
  // reset() (so "New session" naturally drops back to the empty state).
  guideOpen: boolean;

  // Optimistic pending entries for any action that will eventually
  // produce a document. Drives the immediate transition from
  // EmptyState to the working layout (App.tsx reads .length), and the
  // placeholder rows in DocumentRail.
  pendingEntries: PendingEntry[];

  // Actions
  reset: () => void;
  setSessionId: (id: string | null) => void;
  // Point this tab at a different existing session (debug switcher).
  // Wipes the previous session's agent output / chat / UI state, then
  // binds to the new id so react-query refetches everything fresh.
  switchSession: (id: string) => void;
  setView: (view: "analysis" | "letter") => void;
  setSideTab: (tab: "documents" | "fields" | "generate") => void;
  selectDoc: (id: string | null) => void;
  setViewerTab: (id: string, tab: Tab) => void;
  openAppealTypeDialog: (
    target?: { mode: "single"; kind: string } | { mode: "bundle" },
  ) => void;
  closeAppealTypeDialog: () => void;
  setDrawer: (which: "rail" | "agent" | "chat", open: boolean) => void;
  setGuideOpen: (open: boolean) => void;
  setAgentStatus: (s: AgentStatus) => void;
  setAgentResult: (r: {
    status: AgentStatus;
    appealType?: AppealType | null;
    appealTypeRationale?: string | null;
    pendingRequest?: DocumentRequest | null;
    analysisSummary?: string | null;
    letterHtml?: string | null;
    currentRevisionId?: string | null;
  }) => void;
  setLetterHtml: (html: string) => void;
  appendRefinementMessage: (m: ChatMessage) => void;
  // Freeze a live ``activity`` stream item into a static snapshot once
  // its agent run has returned.
  freezeActivity: (activityId: string, progress: ChatMessage["progress"]) => void;
  // Consume an ``action`` stream item: hide its button and replace it with
  // a descriptive line.
  consumeAction: (actionId: string, label: string) => void;
  // Morph an ``action`` stream item into a live ``task`` (upload/synth in
  // flight), then resolve it to a done/error line.
  morphActionToTask: (actionId: string, label: string) => void;
  completeTask: (actionId: string, label: string, error?: boolean) => void;
  addPending: (entry: PendingEntry) => void;
  removePending: (token: string) => void;

  // Revision actions
  appendRevision: (rev: LetterRevisionInfo) => void;
  setCurrentRevision: (revisionId: string, html: string) => void;
  setLetterMode: (mode: LetterMode) => void;
  setDiffingRevision: (revisionId: string | null) => void;
}

const initial = {
  sessionId: null,
  agentStatus: "idle" as AgentStatus,
  appealType: null,
  appealTypeRationale: null,
  analysisSummary: null,
  letterHtml: null,
  pendingRequest: null,
  refinementMessages: [] as ChatMessage[],
  revisions: [] as LetterRevisionInfo[],
  currentRevisionId: null,
  letterMode: "letter" as LetterMode,
  diffingRevisionId: null,
  view: "analysis" as const,
  sideTab: "documents" as const,
  selectedDocId: null,
  viewerTabByDoc: {} as Record<string, Tab>,
  appealTypeDialogOpen: false,
  appealTypePickerTarget: null,
  drawerOpen: { rail: false, agent: false, chat: false },
  guideOpen: false,
  pendingEntries: [] as PendingEntry[],
};

export const useAppStore = create<AppState>((set) => ({
  ...initial,

  reset: () => set(initial),
  setSessionId: (sessionId) => set({ sessionId }),
  switchSession: (sessionId) => set({ ...initial, sessionId }),

  setView: (view) => set({ view }),
  setSideTab: (sideTab) => set({ sideTab }),
  selectDoc: (id) => set({ selectedDocId: id }),

  setViewerTab: (id, tab) =>
    set((state) => ({
      viewerTabByDoc: { ...state.viewerTabByDoc, [id]: tab },
    })),

  openAppealTypeDialog: (target) =>
    set({
      appealTypeDialogOpen: true,
      appealTypePickerTarget: target ?? null,
    }),
  closeAppealTypeDialog: () => set({ appealTypeDialogOpen: false, appealTypePickerTarget: null }),

  setDrawer: (which, open) =>
    set((state) => ({ drawerOpen: { ...state.drawerOpen, [which]: open } })),

  setGuideOpen: (guideOpen) => set({ guideOpen }),

  setAgentStatus: (agentStatus) => set({ agentStatus }),
  setAgentResult: (r) =>
    set({
      agentStatus: r.status,
      appealType: r.appealType ?? null,
      appealTypeRationale: r.appealTypeRationale ?? null,
      pendingRequest: r.pendingRequest ?? null,
      analysisSummary: r.analysisSummary ?? null,
      letterHtml: r.letterHtml ?? null,
      currentRevisionId: r.currentRevisionId ?? null,
    }),
  setLetterHtml: (letterHtml) => set({ letterHtml }),
  appendRefinementMessage: (m) =>
    set((state) => ({
      refinementMessages: [...state.refinementMessages, m],
    })),
  freezeActivity: (activityId, progress) =>
    set((state) => ({
      refinementMessages: state.refinementMessages.map((m) =>
        m.activityId === activityId ? { ...m, live: false, progress } : m,
      ),
    })),
  consumeAction: (actionId, label) =>
    set((state) => ({
      refinementMessages: state.refinementMessages.map((m) =>
        m.actionId === actionId ? { ...m, consumed: true, content: label } : m,
      ),
    })),
  morphActionToTask: (actionId, label) =>
    set((state) => ({
      refinementMessages: state.refinementMessages.map((m) =>
        m.actionId === actionId
          ? { ...m, kind: "task", consumed: true, live: true, error: false, content: label }
          : m,
      ),
    })),
  completeTask: (actionId, label, error) =>
    set((state) => ({
      refinementMessages: state.refinementMessages.map((m) =>
        m.actionId === actionId
          ? { ...m, kind: "task", live: false, error: !!error, content: label }
          : m,
      ),
    })),

  addPending: (entry) =>
    set((state) => ({ pendingEntries: [...state.pendingEntries, entry] })),
  removePending: (token) =>
    set((state) => ({
      pendingEntries: state.pendingEntries.filter((e) => e.token !== token),
    })),

  appendRevision: (rev) =>
    set((state) => ({
      revisions: [...state.revisions, rev],
    })),
  setCurrentRevision: (revisionId, html) =>
    set({
      currentRevisionId: revisionId,
      letterHtml: html,
      letterMode: "letter",
      diffingRevisionId: null,
    }),
  setLetterMode: (letterMode) => set({ letterMode }),
  setDiffingRevision: (diffingRevisionId) =>
    set({
      diffingRevisionId,
      letterMode: diffingRevisionId ? "diff" : "letter",
    }),
}));
